import React, { useState } from "react";
import { toast } from "sonner";
import {
    X,
    DotsSixVertical,
    ArrowClockwise,
    Check,
} from "@phosphor-icons/react";

/**
 * KanbanCompactSettings — V2.1.1 iter170.
 *
 * Drag-to-reorder modal for the compact-mode badge priority. Ships the
 * ordered list of catalogue keys back to the parent, which persists it
 * to localStorage via `saveCompactOrder()`.
 *
 * Props:
 *   open       — bool
 *   onClose    — () => void
 *   order      — string[] current ordering (highest priority first)
 *   labels     — { [key]: string } display labels
 *   onSave     — (newOrder: string[]) => void
 *   onReset    — () => void (revert to defaults)
 */
export default function KanbanCompactSettings({ open, onClose, order, labels, onSave, onReset }) {
    const [items, setItems] = useState(order);
    const [dragIdx, setDragIdx] = useState(null);

    if (!open) return null;

    const move = (from, to) => {
        if (from === to || to < 0 || to >= items.length) return;
        const next = items.slice();
        const [x] = next.splice(from, 1);
        next.splice(to, 0, x);
        setItems(next);
    };

    const save = () => {
        onSave(items);
        toast.success("Compact badge order saved");
        onClose();
    };

    return (
        <div
            data-testid="kanban-compact-settings"
            className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
        >
            <div
                className="bg-white border-2 border-ink w-full max-w-md max-h-[85vh] overflow-y-auto"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between p-5 border-b-2 border-ink">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt">
                            Compact mode
                        </p>
                        <h3 className="font-heading text-xl font-black tracking-tighter text-ink mt-1">
                            Reorder compact badges
                        </h3>
                        <p className="text-xs text-[#525252] mt-1">
                            Drag to change which 3 badges win the top slots on
                            every card. Priority runs top → bottom.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="compact-settings-close"
                        className="p-1 hover:bg-neutral-100"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                <div className="p-5 space-y-1">
                    {items.map((k, i) => (
                        <div
                            key={k}
                            draggable
                            onDragStart={() => setDragIdx(i)}
                            onDragEnd={() => setDragIdx(null)}
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={() => { if (dragIdx != null) move(dragIdx, i); }}
                            data-testid={`compact-settings-row-${k}`}
                            className={`flex items-center gap-3 border px-3 py-2 bg-white cursor-grab active:cursor-grabbing transition ${
                                dragIdx === i ? "border-cobalt bg-cobalt/5" : "border-[#E5E5E5] hover:border-ink"
                            }`}
                        >
                            <DotsSixVertical size={16} weight="bold" className="text-[#A3A3A3] shrink-0" />
                            <span className="font-mono text-[10px] tabular-nums text-[#A3A3A3] w-6">
                                {String(i + 1).padStart(2, "0")}
                            </span>
                            <span className="text-sm font-bold text-ink flex-1 truncate">
                                {labels[k] || k}
                            </span>
                            {i < 3 && (
                                <span className="text-[9px] font-black uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5">
                                    Top {i + 1}
                                </span>
                            )}
                            <div className="flex flex-col gap-0.5">
                                <button
                                    type="button"
                                    onClick={() => move(i, i - 1)}
                                    disabled={i === 0}
                                    className="text-[10px] font-black text-[#525252] hover:text-ink disabled:opacity-30 px-1"
                                    data-testid={`compact-settings-up-${k}`}
                                >▲</button>
                                <button
                                    type="button"
                                    onClick={() => move(i, i + 1)}
                                    disabled={i === items.length - 1}
                                    className="text-[10px] font-black text-[#525252] hover:text-ink disabled:opacity-30 px-1"
                                    data-testid={`compact-settings-down-${k}`}
                                >▼</button>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="p-5 border-t-2 border-ink flex items-center gap-2 sticky bottom-0 bg-white">
                    <button
                        onClick={() => { onReset(); onClose(); }}
                        data-testid="compact-settings-reset"
                        className="inline-flex items-center gap-1 border border-[#E5E5E5] hover:border-ink px-3 py-2 text-xs font-black uppercase tracking-wider text-[#525252]"
                    >
                        <ArrowClockwise size={12} weight="bold" /> Reset defaults
                    </button>
                    <button
                        onClick={save}
                        data-testid="compact-settings-save"
                        className="ml-auto inline-flex items-center gap-1 bg-ink hover:bg-cobalt text-white px-4 py-2 text-xs font-black uppercase tracking-wider transition"
                    >
                        <Check size={12} weight="bold" /> Save order
                    </button>
                </div>
            </div>
        </div>
    );
}
